import { lazy, Suspense, useMemo } from "react";

import type { Rule, Target } from "@/lib/rules/ruleTypes";
import {
  formatEnforcementRemainingMs,
  msUntilBreakEnds,
  msUntilEnforcementEnds,
} from "@/lib/rules/ruleSchedule";
import { useScreen0Language } from "@/features/language";
import { formatTemplate, getTranslations } from "@/i18n";
import type { ActiveEnforcementShaderVariant } from "./ActiveEnforcementShader";

const MAX_VISIBLE_NAMES = 3;

const ActiveEnforcementShader = lazy(() =>
  import("./ActiveEnforcementShader").then((module) => ({
    default: module.ActiveEnforcementShader,
  })),
);

function ruleDisplayName(
  rule: Rule,
  targetsById: Map<string, Target>,
  noTargetsLabel: string,
): string {
  const ruleTargets = rule.targetIds
    .map((id) => targetsById.get(id))
    .filter((target): target is Target => Boolean(target));
  return ruleTargets.map((target) => target.name).join(", ") || noTargetsLabel;
}

function formatRuleNameList(
  names: string[],
  moreTemplate: string,
): string {
  if (names.length <= MAX_VISIBLE_NAMES) {
    return names.join(", ");
  }
  const visible = names.slice(0, MAX_VISIBLE_NAMES).join(", ");
  const remaining = names.length - MAX_VISIBLE_NAMES;
  return `${visible}, ${formatTemplate(moreTemplate, { count: remaining })}`;
}

type ActiveEnforcementBannerProps = {
  rules: Rule[];
  targetsById: Map<string, Target>;
  now: Date;
  prefersReducedMotion: boolean;
  variant?: Extract<ActiveEnforcementShaderVariant, "enforcement" | "pause">;
};

export function StaticShaderFallback({
  variant = "enforcement",
}: {
  variant?: Extract<ActiveEnforcementShaderVariant, "enforcement" | "pause">;
}) {
  const background =
    variant === "pause"
      ? "linear-gradient(135deg, oklch(62% 0.12 65) 0%, oklch(42% 0.10 58) 55%, oklch(28% 0.05 62) 100%)"
      : "linear-gradient(135deg, oklch(28% 0.05 155) 0%, oklch(16% 0.02 165) 55%, oklch(12% 0.015 160) 100%)";

  return (
    <div
      className="absolute inset-0"
      style={{ background }}
      aria-hidden
    />
  );
}

export function ActiveEnforcementBanner({
  rules,
  targetsById,
  now,
  prefersReducedMotion,
  variant = "enforcement",
}: ActiveEnforcementBannerProps) {
  const t = getTranslations(useScreen0Language().languageCode);

  const ruleNames = useMemo(
    () => rules.map((rule) => ruleDisplayName(rule, targetsById, t.rules.noTargets)),
    [rules, targetsById, t.rules.noTargets],
  );

  const isPause = variant === "pause";
  const remainingMs = isPause ? msUntilBreakEnds(rules, now) : msUntilEnforcementEnds(rules, now);
  const endsInText = formatTemplate(
    isPause ? t.rules.enforcementBreakBannerEndsIn : t.rules.enforcementBannerEndsIn,
    { time: formatEnforcementRemainingMs(remainingMs) },
  );
  const body = isPause ? null : formatRuleNameList(ruleNames, t.rules.enforcementBannerMore);
  const overlayClass = isPause
    ? "pointer-events-none absolute inset-0 rounded-[inherit] bg-[oklch(22%_0.04_62/0.58)] backdrop-blur-[2px]"
    : "pointer-events-none absolute inset-0 rounded-[inherit] bg-[oklch(12%_0.015_160/0.58)] backdrop-blur-[2px]";
  const titleClass = isPause
    ? "text-[11px] font-semibold uppercase tracking-[0.12em] text-[oklch(78%_0.12_68)]"
    : "text-[11px] font-semibold uppercase tracking-[0.12em] text-[oklch(72%_0.09_155)]";
  const countdownClass = isPause
    ? "mono text-xs font-semibold tabular-nums text-[oklch(82%_0.10_68)]"
    : "mono text-xs font-semibold tabular-nums text-[oklch(78%_0.06_155)]";

  return (
    <div
      className="liquid-glass-panel relative h-32 w-full overflow-hidden sm:h-36 md:h-40"
      aria-hidden={false}
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden rounded-[inherit]">
        {prefersReducedMotion ? (
          <StaticShaderFallback variant={variant} />
        ) : (
          <Suspense fallback={<StaticShaderFallback variant={variant} />}>
            <ActiveEnforcementShader animate={!prefersReducedMotion} variant={variant} />
          </Suspense>
        )}
      </div>

      <div className={overlayClass} aria-hidden />

      <div
        role="status"
        aria-live="polite"
        className="pointer-events-none relative z-10 flex h-full flex-col justify-center gap-1 px-4 py-3"
      >
        <p className={titleClass}>
          {isPause ? t.rules.enforcementBreakBannerTitle : t.rules.enforcementBannerTitle}
        </p>
        <p className={countdownClass}>{endsInText}</p>
        {body ? (
          <p className="line-clamp-2 text-sm font-medium leading-snug text-[oklch(90%_0.02_150)]">
            {body}
          </p>
        ) : null}
      </div>
    </div>
  );
}
